import React, { useState, useEffect, useCallback, useRef } from 'react';
import Loader from './components/UI/Loader/Loader';
import RedirectNotice from './components/UI/RedirectNotice/RedirectNotice';
import axios from './axios-instance';
import ContentDetails from './components/ContentDetails/ContentDetails';
import actv8Logo from './images/actv8me-logo.png';
import Button from './components/UI/Button/Button';
import scratchImage from './images/swipe-to-reveal-v2.png';
import { useSelector, useDispatch } from 'react-redux';
import {
    setAndroidPassDetails,
    setContentItem,
    setContentResponse,
    setTerms,
} from './store/contentSlice';
import { DEV_MODE, devLogImpression } from './dev/devMode';

const Content = (props) => {
    const {
        loading,
        showActv8,
        setShowActv8,
        forwardParams,
        device,
        hasSlug,
        location,
        preciseLocation,
        catchUrlSearch,
        setShowError,
        setLoading,
        timestamp
    } = props;
    const [showScratcherOverlay, setShowScratcherOverlay] = useState(null);
    const [scratcherOverlayImage, setScratcherOverlayImage] = useState(null);
    // Auto-redirect (a8_redirect=1) target. When set, the endless loader is
    // replaced by a "you're being redirected" interstitial with a manual
    // fallback link, while the impression + navigation happen in the background.
    const [redirectUrl, setRedirectUrl] = useState(null);
    // Pending time-delayed-delivery timers (getTimestampTargets); cleared on unmount.
    const timestampTimers = useRef([]);
    // Fired-once guard for the content catch. The effect below re-runs on deps
    // churn (fresh callback/object identities each render), which fired the
    // deliver POST + view impression 6-8x per load — inflating analytics and
    // hammering the backend. One catch per (trigger params + device) identity.
    const catchFiredKey = useRef(null);

    const dispatch = useDispatch();
    const content = useSelector((state) => state.content);

    // Strip the a8_redirect flag from a target URL. Shared by the actual
    // navigation and the interstitial's manual "tap here" fallback link, so
    // both always point at the same cleaned destination.
    const cleanRedirectUrl = (url = '') =>
        url
            .replace('?a8_redirect=1', '')
            .replace('?a8_redirect=0', '')
            .replace('&a8_redirect=1', '')
            .replace('&a8_redirect=0', '');

    const onRedirectUrl = (url) => {
        window.location.href = cleanRedirectUrl(url);
    };
    const updateStatus = useCallback(
        async (status, content) => {
            // Dev mode: record the impression in the in-page log, run the same
            // redirect side-effect, but never write to the backend.
            if (DEV_MODE) {
                devLogImpression(status);
                onRedirectUrl(content?.url);
                return;
            }
            try {
                const uri = `/device/${device?.deviceDetails?.uuid}/content/${content?.id}?status=${status}`;
                const res = await axios.put(
                    uri,
                    {},
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'x-device-uuid': `${device?.deviceDetails?.uuid}`,
                        },
                    }
                );
                if (res?.status === 200) {
                    if (res?.data?.message) {
                        console.log('STATUS: ', status + ' ', res.data.message);
                    }
                }
                onRedirectUrl(content.url);
            } catch (err) {
                onRedirectUrl(content.url);
                console.error('Error: ', err);
            }
        },
        [device]
    );

    const checkScratcher = useCallback((data) => {
        if (data?.scratcher?.enabled) {
            let scratcherImage = data?.scratcher?.images?.overlay?.url
                ? getCdnPath(data.scratcher.images.overlay.url)
                : scratchImage;
            //let scratcherImage = data.scratcher.images && data.scratcher.images.overlay && data.scratcher.images.overlay.url ? data.scratcher.images.overlay.url : scratchImage;
            setShowScratcherOverlay(true);
            setScratcherOverlayImage(scratcherImage);
        } else {
            setShowScratcherOverlay(false);
        }
    }, []);

    const getCdnPath = (url) =>
        url.replace(
            'https://s3.amazonaws.com/cdn.actv8me.com/',
            'https://cdn.actv8me.com/'
        );

    const getContentTerms = useCallback(
        async (id, device) => {
            try {
                let res = await axios.get(`content-terms/` + id, {
                    headers: {
                        'x-device-uuid': `${device?.uuid}`,
                    },
                });
                if (res) {
                    let terms = String(res.data.body);
                    dispatch(setTerms(terms));
                }
            } catch (err) {
                console.log('Error: ', err);
            }
        },
        [dispatch]
    );

    const postAndroidPass = useCallback(
        async (id, device) => {
            try {
                const res = await axios.post(
                    `/device/${device?.id}/content/${id}/androidpass`,
                    {
                        forward_url_params: forwardParams,
                    },
                    {
                        headers: {
                            'x-device-uuid': `${device?.uuid}`,
                        },
                    }
                );
                if (res) {
                    dispatch(
                        setAndroidPassDetails({
                            androidPass: true,
                            jwt: res.data.jwt.jwt,
                        })
                    );
                    setLoading(false);
                }
            } catch (err) {
                console.log('Error :', err);
                setLoading(false);
            }
        },
        [dispatch, forwardParams, setLoading]
    );

    const onSetContentItem = useCallback(
        (content, device) => {
            let end;
            let isBuyInStore;
            let tandc = 'Terms & Conditions';
            let exp = 'Expired';
            let termID = content?.content_term_id;
            let showDownload = false;
            let buyNowText = 'Get Offer';
            let buyInStoreText = 'Get In-Store Barcode';
            let clickThroughBtnOne;
            let clickThroughBtnTwo;

            if (content?.buttons?.length > 0) {
                const buttons = content.buttons.slice();
                const buyNowButtons = buttons.filter(
                    (button) =>
                        button.key === 'redeem_online_btn' &&
                        button.locale === 'en'
                );
                const buyInStoreButton = buttons.filter(
                    (button) =>
                        button.key === 'redeem_in_store_btn' &&
                        button.locale === 'en'
                );
                const clickThroughBtn1 = buttons.filter(
                    (button) =>
                        button.key === 'click_through_btn_1' &&
                        button.locale === 'en'
                );
                const clickThroughBtn2 = buttons.filter(
                    (button) =>
                        button.key === 'click_through_btn_2' &&
                        button.locale === 'en'
                );

                if (buyNowButtons?.length > 0) {
                    buyNowText = buyNowButtons[0].label;
                }
                if (buyInStoreButton?.length > 0) {
                    buyInStoreText = buyInStoreButton[0].label;
                }
                if (clickThroughBtn1?.length > 0) {
                    console.log(clickThroughBtn1[0]);
                    clickThroughBtnOne = clickThroughBtn1[0];
                }
                if (clickThroughBtn2?.length > 0) {
                    clickThroughBtnTwo = clickThroughBtn2[0];
                }
            }

            if (content?.redemption !== null) {
                if (
                    content?.redemption?.method === 2 ||
                    content?.redemption?.method === 3
                ) {
                    isBuyInStore = true;
                } else if (
                    content?.redemption?.method === 1 &&
                    content?.redemption?.url
                ) {
                    let url = new URL(content.redemption.url).search.split('&');
                    let isDownload =
                        url[0].split('?action=').join('') === 'download';
                    showDownload = isDownload;
                } else {
                    isBuyInStore = false;
                }
            } else {
                isBuyInStore = false;
            }

            if (content?.end_at === null || content?.end_at === undefined) {
                end = 'Never';
            } else {
                // Space→T keeps "YYYY-MM-DD HH:mm:ss" parseable in Safari too.
                // Downstream only checks `endDate === 'Expired'` (see ContentDetails).
                const endAt = new Date(String(content?.end_at).replace(' ', 'T'));
                end = endAt < new Date() ? exp : 'active';
            }

            // Set redemption URL
            let redemptionURL =
                content?.redemption !== null &&
                content?.redemption?.url
                    ? content?.redemption.url
                    : '';

            // Appends forward params (i.e. macros) based on env setting
            if (
                redemptionURL !== '' &&
                process.env.REACT_APP_FORWARD_PARAMS === 'true'
            ) {
                redemptionURL += redemptionURL.indexOf('?') > -1 ? '&' : '?';
                redemptionURL += forwardParams;
            }
            const c = {
                id: content?.id,
                hero: getCdnPath(content?.images.hero.url),
                title: content?.display_name,
                brand: content?.brand,
                description: content?.description,
                buyOnline: redemptionURL,
                contentTermId: termID || '',
                contentTerms: tandc,
                endDate: end,
                buyInStore: isBuyInStore,
                showBuyInStore: false,
                toggleTerms: false,
                type: content?.type,
                showContent: true,
                content: content,
                applePass: content?.apple_pass_enabled,
                showDownload: showDownload,
                buyNowText: buyNowText,
                buyInStoreText: buyInStoreText,
                clickThroughBtnOne: clickThroughBtnOne,
                clickThroughBtnTwo: clickThroughBtnTwo,
            };
            dispatch(setContentItem(c));
            if (termID && termID !== null) {
                getContentTerms(termID, device);
            }
            if (content?.android_pass_enabled) {
                postAndroidPass(content?.id, device);
            } else {
                setLoading(false);
            }
        },
        [dispatch, forwardParams, getContentTerms, postAndroidPass, setLoading]
    );

    const catchContent = useCallback(
        (res, device) => {
            console.log('catching content...');
            try {
                if (res?.data?.[0]) {
                    dispatch(setContentResponse(res?.data?.[0]));
                    if (
                        res.data[0].type === 'offer' ||
                        res.data[0].type === 'prize' ||
                        res.data[0].type === 'url' ||
                        res.data[0].type === 'message'
                    ) {
                        if (res.data[0].type === 'url' && res.data[0].url) {
                            const parsedContentUrl = new URL(res.data[0].url);

                            if (
                                parsedContentUrl.searchParams.has('a8_redirect') || parsedContentUrl.hash.indexOf('a8_redirect') > -1
                            ) {                                
                                if (
                                    Number(
                                        parsedContentUrl.searchParams.get(
                                            'a8_redirect'
                                        )
                                    ) === 1 ||
                                    // Exact-value match — a bare indexOf would
                                    // also fire on e.g. #a8_redirect=10.
                                    /(?:^#|[#&;])a8_redirect=1(?:&|$)/.test(parsedContentUrl.hash)
                                ) {
                                    // Show the redirect interstitial (instead of
                                    // an endless loader) while the impression +
                                    // navigation run.
                                    setRedirectUrl(cleanRedirectUrl(res.data[0].url));
                                    updateStatus(1, res.data[0]);
                                    return;
                                }
                            }
                        }
                        checkScratcher(res.data[0]);
                        onSetContentItem(res.data[0], device);
                    } else {
                        setShowError({
                            showError: true,
                            errorCode: 400,
                        });
                        setLoading(false);
                    }
                } else {
                    setShowError({
                        showError: true,
                        errorCode: res.status,
                    });
                    setLoading(false);
                }
            } catch (err) {
                console.error('Error: ', err);
                if (err.message) {
                    setShowError({
                        showError: true,
                        errorCode: err.status,
                    });
                    setLoading(false);
                } else {
                    setShowError({
                        showError: true,
                        errorCode: 400,
                    });
                    setLoading(false);
                }
            }
        },
        [
            checkScratcher,
            dispatch,
            onSetContentItem,
            setLoading,
            setShowError,
            updateStatus,
        ]
    );

    const catchContentByTriggerType = useCallback(
        async (
            triggerable_type,
            triggerable_id,
            client_id,
            device
        ) => {
            try {
                !loading && setLoading(true);
                const res = await axios.post(
                    `${triggerable_type}/${triggerable_id}/catch?client_id=${client_id}`,
                    {},
                    {
                        headers: {
                            'x-device-uuid': `${device?.uuid}`,
                        },
                    }
                );
                catchContent(res, device);
            } catch (err) {
                console.log('err catching content ', err);
                setShowError({ showError: true, errorCode: err?.response?.status || err?.status });
                setLoading(false);
            }
        },
        [catchContent, loading, setLoading, setShowError]
    );

    const catchContentByTriggerUuid = useCallback(
        async (trigger_uuid, device, payload = null) => {
            try {
                !loading && setLoading(true);
                const res = await axios.post(
                    `content/deliver?trigger_uuid=${trigger_uuid}`,
                    payload,
                    {
                        headers: {
                            'x-device-uuid': `${device?.uuid}`,
                        },
                    }
                );
                catchContent(res, device);

            } catch (err) {
                console.log('err catching content ', err);
                setShowError({ showError: true, errorCode: err?.response?.status || err?.status });
                setLoading(false);
            }
        },
        [catchContent, loading, setLoading, setShowError]
    );

    const getTimestampTargets = useCallback(
        async(trigger_uuid) => {
            console.log('********* Requesting timestamps ******** ');

            const res = await axios.get(
                `trigger/timestamps?uuid=${trigger_uuid}`,
                [],
                {
                    headers: {
                        'x-device-uuid': `${device?.uuid}`,
                    },
                }
            );

            if(res && res.data) {
                console.log('========= Retrieved timestamps =======');
                const targetTimestamps = res.data;
                for(let item of targetTimestamps) {
                    if(item.value > timestamp) {
                        console.log('    will ping in ' + (item.value - timestamp) + ' seconds');

                        const timerId = setTimeout(() => {
                                const payload = {timestamp : (item.value + 1)};
                                catchContentByTriggerUuid(trigger_uuid, device.deviceDetails, payload);
                        }, (item.value - timestamp) * 1000);
                        timestampTimers.current.push(timerId);
                            
                    }
                } 

            }
        },[timestamp, device, catchContentByTriggerUuid]);
    

    useEffect(() => {
        const getTriggerId = (device) => {
            console.log('getting trigger id ...');
            console.log('has slug? ' + hasSlug);
            console.log('location search: ' + location.search);
            if (hasSlug !== null && device?.id) {
                let paramsString = hasSlug ? catchUrlSearch : location.search;
                // console.log('Params string: ' + paramsString);
                const catchKey = `${paramsString}|${device.id}|${timestamp ?? ''}`;
                if (catchFiredKey.current === catchKey) return;
                catchFiredKey.current = catchKey;
                const params = new URLSearchParams(paramsString);
                if (
                    params.has('triggerable_id') &&
                    params.has('client_id') &&
                    params.has('triggerable_type')
                ) {
                    let triggerable_id = params.get('triggerable_id');
                    let triggerable_type = params.get('triggerable_type');
                    let client_id = params.get('client_id');
                    if (
                        triggerable_type &&
                        triggerable_type !== null &&
                        triggerable_id &&
                        triggerable_id !== null &&
                        client_id &&
                        client_id !== null
                    ) {
                        catchContentByTriggerType(
                            triggerable_type,
                            triggerable_id,
                            client_id,
                            device
                        );
                    }
                } else if (params.has('trigger_uuid')) {
                    const trigger_uuid = params.get('trigger_uuid');
                    let payload = {};
                    if(timestamp) {
                        payload.timestamp = timestamp
                    }

                    if(preciseLocation) {
                        payload.preciseLocation = {
                            'latitude' : preciseLocation.coords.latitude,
                            'longitude' : preciseLocation.coords.longitude
                        }
                        
                        if(process.env.REACT_APP_PRECISE_LOCATION_MAX_DISTANCE){
                            payload.preciseLocation.maxDistance = parseFloat(process.env.REACT_APP_PRECISE_LOCATION_MAX_DISTANCE);
                        }

                    } else if(process.env.REACT_APP_PRECISE_LOCATION_MAX_DISTANCE) {
                        payload.preciseLocation = {
                            "maxDistance" : parseFloat(process.env.REACT_APP_PRECISE_LOCATION_MAX_DISTANCE)
                        }
                    }

                    catchContentByTriggerUuid(trigger_uuid, device, payload);
                    if(timestamp) {
                        getTimestampTargets(trigger_uuid);
                    }                    
                } else {
                    console.log('Missing params...');
                }
            }
        };
        device?.deviceDetails?.id && getTriggerId(device?.deviceDetails);
    }, [
        catchContentByTriggerType,
        catchContentByTriggerUuid,
        catchUrlSearch,
        device?.deviceDetails,
        device?.deviceDetails?.id,
        hasSlug,
        location.search,
        timestamp,
        getTimestampTargets
    ]);

    // Clear any pending time-delayed delivery timers when Content unmounts.
    useEffect(() => () => {
        timestampTimers.current.forEach(clearTimeout);
        timestampTimers.current = [];
    }, []);

    return (
        <div>
            {redirectUrl ? (
                <RedirectNotice url={redirectUrl} />
            ) : loading ||
            showScratcherOverlay === null ||
            !content?.id ||
            content?.id === null ? (
                <div className='LoadContainer'>
                    <div className='Loading'>
                        <Loader />
                    </div>
                </div>
            ) : (
                <div>
                    {showActv8 ? (
                        <div>
                            <div className='LogoContainer'>
                                <img
                                    className='Actv8'
                                    src={actv8Logo}
                                    alt='actv8_logo'
                                />
                                <div>
                                    <Button
                                        onClick={() => setShowActv8(true)}
                                        btnText={'Go Back To Offer'}></Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <ContentDetails
                            open={true}
                            onNoThanks={() => setShowActv8(false)}
                            scratcher={showScratcherOverlay}
                            scratcherOverlayImage={scratcherOverlayImage}
                            forwardParams={forwardParams}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default Content;